/**
 * Session memory (1.I-1 deliverable 3): the signed-in user, remembered when the route guard's
 * `auth.me` answers and forgotten on a 401 or a sign-out, so the frame (which sits above every
 * route) can decide without asking again. Never persisted: it lives as long as the tab.
 */
import type { MeResponse } from '@codex/contracts';
import { useSyncExternalStore } from 'react';

let current: MeResponse | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

export function rememberMe(me: MeResponse): void {
  current = me;
  emit();
}

export function forgetMe(): void {
  if (current === null) return;
  current = null;
  emit();
}

export function currentMe(): MeResponse | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The remembered user, re-rendering when it changes. */
export function useShellSession(): MeResponse | null {
  return useSyncExternalStore(subscribe, currentMe, () => null);
}

let signingOut = false;

/** Set when a deliberate sign-out starts, so the 401 its dead session causes is not a redirect of its own. */
export function beginSignOut(): void {
  signingOut = true;
}

export function isSigningOut(): boolean {
  return signingOut;
}
